import { useState } from 'react'
import { Field } from '../components/ui.js'

export type Recurrence =
  | { type: 'weekly'; weekdays: number[] }
  | { type: 'interval_weeks'; weekdays: number[]; intervalWeeks: number; anchorDate: string }
  | { type: 'monthly_nth_weekday'; nth: 1 | 2 | 3 | 4 | -1; weekday: number }
  | { type: 'monthly_day'; day: number }
  | { type: 'once'; date: string }

/** Monday first, the German working week, not the JS week. */
const WEEKDAYS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 0, label: 'So' },
]

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

export function RecurrenceEditor({
  value,
  onChange,
}: {
  value: Recurrence
  onChange: (next: Recurrence) => void
}) {
  const weekdays = 'weekdays' in value ? value.weekdays : []

  function toggleWeekday(day: number) {
    if (!('weekdays' in value)) return
    const next = weekdays.includes(day) ? weekdays.filter((d) => d !== day) : [...weekdays, day]
    // At least one day must stay selected or the plan would never fire.
    if (next.length === 0) return
    onChange({ ...value, weekdays: next })
  }

  // "Täglich" is a weekly rule with all seven days, so nothing downstream changes.
  const daily = value.type === 'weekly' && weekdays.length === 7

  function switchType(type: Recurrence['type'] | 'daily') {
    const today = new Date().toISOString().slice(0, 10)
    switch (type) {
      case 'daily':
        return onChange({ type: 'weekly', weekdays: [1, 2, 3, 4, 5, 6, 0] })
      case 'weekly':
        return onChange({ type: 'weekly', weekdays: weekdays.length && weekdays.length < 7 ? weekdays : [2] })
      case 'interval_weeks':
        return onChange({
          type: 'interval_weeks',
          weekdays: weekdays.length ? weekdays : [2],
          intervalWeeks: 2,
          anchorDate: today,
        })
      case 'monthly_nth_weekday':
        return onChange({ type: 'monthly_nth_weekday', nth: 1, weekday: 1 })
      case 'monthly_day':
        return onChange({ type: 'monthly_day', day: 1 })
      case 'once':
        return onChange({ type: 'once', date: today })
    }
  }

  return (
    <>
      <Field label="Rhythmus">
        <select value={daily ? 'daily' : value.type} onChange={(e) => switchType(e.target.value as Recurrence['type'] | 'daily')}>
          <option value="daily">Täglich</option>
          <option value="weekly">Wöchentlich an festen Tagen</option>
          <option value="interval_weeks">Alle n Wochen</option>
          <option value="monthly_nth_weekday">Monatlich am n-ten Wochentag</option>
          <option value="monthly_day">Monatlich an einem festen Tag</option>
          <option value="once">Einmalig</option>
        </select>
      </Field>

      {'weekdays' in value && !daily && (
        <div className="field">
          <span>Wochentage</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {WEEKDAYS.map((day) => {
              const on = weekdays.includes(day.value)
              return (
                <button
                  key={day.value}
                  type="button"
                  className={`btn ${on ? '' : 'secondary'} small`}
                  style={{ minWidth: 46 }}
                  aria-pressed={on}
                  onClick={() => toggleWeekday(day.value)}
                >
                  {day.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {value.type === 'interval_weeks' && (
        <div className="row">
          <Field label="Abstand in Wochen">
            <input
              type="number"
              min={1}
              max={12}
              value={value.intervalWeeks}
              onChange={(e) => onChange({ ...value, intervalWeeks: Math.max(1, Number(e.target.value)) })}
            />
          </Field>
          <Field label="Ab dieser Woche" hint="Legt fest, welche Woche als erste zählt.">
            <input
              type="date"
              value={value.anchorDate}
              onChange={(e) => onChange({ ...value, anchorDate: e.target.value })}
            />
          </Field>
        </div>
      )}

      {value.type === 'monthly_nth_weekday' && (
        <div className="row">
          <Field label="Welcher">
            <select
              value={String(value.nth)}
              onChange={(e) => onChange({ ...value, nth: Number(e.target.value) as 1 })}
            >
              <option value="1">erster</option>
              <option value="2">zweiter</option>
              <option value="3">dritter</option>
              <option value="4">vierter</option>
              <option value="-1">letzter</option>
            </select>
          </Field>
          <Field label="Wochentag">
            <select value={value.weekday} onChange={(e) => onChange({ ...value, weekday: Number(e.target.value) })}>
              {WEEKDAYS.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {value.type === 'monthly_day' && (
        <Field label="Tag im Monat" hint="Bei kürzeren Monaten wird der letzte Tag verwendet.">
          <input
            type="number"
            min={1}
            max={31}
            value={value.day}
            onChange={(e) => onChange({ ...value, day: Math.min(31, Math.max(1, Number(e.target.value))) })}
          />
        </Field>
      )}

      {value.type === 'once' && (
        <Field label="Datum">
          <input type="date" value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} />
        </Field>
      )}
    </>
  )
}

/** Optional yearly window, e.g. Gartenpflege only from March to October. */
export function SeasonEditor({
  start,
  end,
  onChange,
}: {
  start: string | null
  end: string | null
  onChange: (start: string | null, end: string | null) => void
}) {
  const [enabled, setEnabled] = useState(Boolean(start && end))

  return (
    <div className="field">
      <span>Saison</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginBottom: 8 }}>
        <input
          type="checkbox"
          style={{ width: 18, height: 18, minHeight: 0 }}
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked)
            onChange(e.target.checked ? (start ?? '03-01') : null, e.target.checked ? (end ?? '10-31') : null)
          }}
        />
        Nur in einem bestimmten Zeitraum des Jahres
      </label>
      {enabled && (
        <div className="row">
          <MonthDayPicker label="von" value={start ?? '03-01'} onChange={(v) => onChange(v, end ?? '10-31')} />
          <MonthDayPicker label="bis" value={end ?? '10-31'} onChange={(v) => onChange(start ?? '03-01', v)} />
        </div>
      )}
      <div className="hint">
        Der Zeitraum darf über den Jahreswechsel gehen, z. B. 01.11. bis 31.03. für Winterleistungen.
      </div>
    </div>
  )
}

function MonthDayPicker({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const month = Number(value.slice(0, 2))
  const day = Number(value.slice(3, 5))
  const daysInMonth = new Date(Date.UTC(2024, month, 0)).getUTCDate()

  return (
    <label className="field" style={{ marginBottom: 0 }}>
      <span>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={day}
          onChange={(e) => onChange(`${String(month).padStart(2, '0')}-${String(e.target.value).padStart(2, '0')}`)}
        >
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}.
            </option>
          ))}
        </select>
        <select
          value={month}
          onChange={(e) => {
            const nextMonth = Number(e.target.value)
            const maxDay = new Date(Date.UTC(2024, nextMonth, 0)).getUTCDate()
            onChange(`${String(nextMonth).padStart(2, '0')}-${String(Math.min(day, maxDay)).padStart(2, '0')}`)
          }}
        >
          {MONTHS.map((name, index) => (
            <option key={name} value={index + 1}>
              {name}
            </option>
          ))}
        </select>
      </div>
    </label>
  )
}
